/**
 * Image text block video popup functionality
 * Handles video thumbnails and creates modal popups for hosted and external videos
 * Supports YouTube, Vimeo, and self-hosted video content with autoplay prevention
 */

class ImageTextBlockVideo extends HTMLElement {
  constructor() {
    super();
    this.videoThumbnails = document.querySelectorAll('.section-image-text-block__video-thumbnail');
    this.init();
  }

  init() {
    this.videoThumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', this.handleThumbnailClick.bind(this));
    });
    
    // Ensure preview videos don't autoplay
    this.preventPreviewAutoplay();
  }

  preventPreviewAutoplay() {
    const previewVideos = document.querySelectorAll('.section-image-text-block__video-preview video');
    previewVideos.forEach(video => {
      video.autoplay = false;
      video.muted = true;
      video.pause();
      
      // Remove autoplay attribute if present
      video.removeAttribute('autoplay');
      
      // Prevent any play events
      video.addEventListener('play', (e) => {
        e.preventDefault();
        video.pause();
      });
    });
  }

  handleThumbnailClick(e) {
    e.preventDefault();
    const thumbnail = e.currentTarget;
    const videoType = thumbnail.dataset.videoPopup;
    
    if (videoType === 'hosted') {
      this.openHostedVideoPopup(thumbnail);
    } else if (videoType === 'external') {
      this.openExternalVideoPopup(thumbnail);
    }
  }

  openHostedVideoPopup(thumbnail) {
    const videoData = thumbnail.querySelector('.section-image-text-block__video-data');
    if (!videoData) return;
    
    const video = videoData.querySelector('video');
    if (!video) return;

    this.createPopup(video.cloneNode(true));
  }

  openExternalVideoPopup(thumbnail) {
    const videoUrl = thumbnail.dataset.videoUrl;
    if (!videoUrl) return;

    const iframe = document.createElement('iframe');
    iframe.src = this.getEmbedUrl(videoUrl);
    iframe.width = '100%';
    iframe.height = '100%';
    iframe.frameBorder = '0';
    iframe.allow = 'autoplay; fullscreen; picture-in-picture';
    iframe.allowFullscreen = true;

    this.createPopup(iframe);
  }

  getEmbedUrl(url) {
    // YouTube
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = this.getYouTubeId(url);
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    }
    
    // Vimeo
    if (url.includes('vimeo.com')) {
      const videoId = this.getVimeoId(url);
      return `https://player.vimeo.com/video/${videoId}?autoplay=1`;
    }
    
    return url;
  }

  getYouTubeId(url) {
    const regex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/gi;
    const match = regex.exec(url);
    return match ? match[1] : '';
  }

  getVimeoId(url) {
    const regex = /vimeo\.com\/(?:.*\/)?([0-9]+)/;
    const match = regex.exec(url);
    return match ? match[1] : '';
  }

  createPopup(mediaElement) {
    // Create overlay
    const overlay = document.createElement('div');
    overlay.className = 'video-popup-overlay';
    
    // Create popup container
    const popup = document.createElement('div');
    popup.className = 'video-popup';
    
    // Create close button
    const closeButton = document.createElement('button');
    closeButton.className = 'video-popup__close';
    closeButton.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 6L6 18" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M6 6L18 18" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    
    // Create video container
    const videoContainer = document.createElement('div');
    videoContainer.className = 'video-popup__container';
    videoContainer.appendChild(mediaElement);
    
    // Assemble popup
    popup.appendChild(closeButton);
    popup.appendChild(videoContainer);
    overlay.appendChild(popup);
    
    // Add to body
    document.body.appendChild(overlay);
    
    // Add active class for animation
    setTimeout(() => {
      overlay.classList.add('active');
    }, 10);
    
    // Start video if it's a video element
    if (mediaElement.tagName === 'VIDEO') {
      mediaElement.play();
    }
    
    // Close handlers
    const closePopup = () => {
      overlay.classList.remove('active');
      setTimeout(() => {
        overlay.remove();
      }, 300);
    };
    
    closeButton.addEventListener('click', closePopup);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closePopup();
      }
    });
    
    // ESC key handler
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        closePopup();
        document.removeEventListener('keydown', handleEsc);
      }
    };
    document.addEventListener('keydown', handleEsc);
  }
}

// Initialize when DOM is ready
if (!customElements.get('image-text-block-video')) {
  customElements.define('image-text-block-video', ImageTextBlockVideo);
}

// Auto-initialize if element exists
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.section-image-text-block__video-thumbnail')) {
    const videoHandler = document.createElement('image-text-block-video');
    document.body.appendChild(videoHandler);
  }
});